import { IAuthAccount } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import { apiClientError, apiLog } from "../src/taskpane/services/ApiService";
import AuthModule from "./AuthModule";

const tryStringify = (value: any): string => {
    let stringifiedObject: string;
    try {
        stringifiedObject = JSON.stringify(value);
    } catch (error) {
        // Ignore
    }
    return stringifiedObject ?? "";
}

// One failure re-triggered by every action posted a row each time (log storms a8bbdb48a,
// ad597b704). A repeat of the same error - same filePath and message once ids, trace ids (the API's
// 400s carry a new one each time) and long numbers are masked - now posts at most once in 5 minutes,
// carrying how many were held back since: the API's OncePerInterval, per page. Status codes stay
// apart (403 and 404 are different problems). Errors naming each item (a bulk action failing per
// file) all differ, so a page also posts at most 30 a minute; the rest are counted and noted on the
// next post.
const REPEAT_INTERVAL_MS = 5 * 60_000;
const MAX_POSTS_PER_MINUTE = 30;
const MAX_TRACKED_ERRORS = 200;
const lastPosted = new Map<string, { at: number; heldBack: number }>();
let minuteStart = 0;
let postsThisMinute = 0;
let othersHeldBack = 0;
// The key each error object was first logged under. ApiService logs a failed call and rethrows it,
// and its callers logged the same error again under their own name (62 catch sites: two rows per
// failure): that posts nothing. Logged again from the same place (a cached failure), it is a repeat.
const loggedErrors = new WeakMap<object, string>();

const errorKey = (filePath: string, error: string): string =>
    (filePath + "|" + error).slice(0, 400)
        .replace(/[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi, "#")
        .replace(/[0-9a-f]{16,}/gi, "#")
        .replace(/\d{4,}/g, "#");

// This error's turn to post: its note, and how to give the turn back when the post doesn't arrive.
// Null when it is only counted.
const takeTurn = (key: string): { note: string; giveBack: () => void } | null => {
    // A clock set back counts as time passed: holding errors until it caught up could take hours.
    const now = Date.now();
    const last = lastPosted.get(key);
    if (last && now >= last.at && now - last.at < REPEAT_INTERVAL_MS) {
        last.heldBack++;
        return null;
    }
    if (now < minuteStart || now - minuteStart >= 60_000) {
        minuteStart = now;
        postsThisMinute = 0;
    }
    if (postsThisMinute >= MAX_POSTS_PER_MINUTE) {
        if (last) {
            last.heldBack++;
        } else {
            othersHeldBack++;
        }
        return null;
    }
    postsThisMinute++;
    if (!last && lastPosted.size >= MAX_TRACKED_ERRORS) {
        lastPosted.clear();
    }
    const turn = { at: now, heldBack: 0 };
    lastPosted.set(key, turn);
    const minute = minuteStart;
    const others = othersHeldBack;
    othersHeldBack = 0;
    let note = last?.heldBack ? ` (${last.heldBack} more in the previous ${Math.round((now - last.at) / 1000)} s)` : "";
    if (others) {
        note += ` (${others} other errors not posted, over ${MAX_POSTS_PER_MINUTE} a minute)`;
    }
    return {
        note,
        // Not posted (signed out, offline, the API down): the next occurrence posts. A repeat's count
        // (this one and what its note held) goes with it; a failed first occurrence is forgotten.
        giveBack: () => {
            if (lastPosted.get(key) === turn) {
                if (last) {
                    lastPosted.set(key, { at: last.at, heldBack: last.heldBack + 1 + turn.heldBack });
                } else {
                    lastPosted.delete(key);
                }
            }
            if (minuteStart === minute) {
                postsThisMinute--;
            }
            othersHeldBack += others;
        },
    };
}

export const logger = async (e: Error, filePath: string, additionalData?: string) => {
    console.error(e);

    // Extract a message without coercing a missing one into the literal string "undefined".
    // (`e?.message + ""` yields "undefined" for message-less/non-Error values, and because that
    //  string is truthy the fallback below could never run - masking the real error.)
    let errorMessage: string = "";
    if (typeof e === "string") {
        errorMessage = e;
    } else if (e?.message) {
        errorMessage = e.message;
    } else if (e) {
        errorMessage = tryStringify(e);
    }

    if (additionalData) {
        errorMessage = errorMessage ? `${errorMessage} - ${additionalData}` : additionalData;
    }

    const error = errorMessage || tryStringify(e);
    const key = errorKey(filePath, error);
    if (typeof e === "object" && e !== null) {
        const firstKey = loggedErrors.get(e);
        if (firstKey === undefined) {
            loggedErrors.set(e, key);
        } else if (firstKey !== key) {
            return;
        }
    }
    // Before the account read: a counted repeat costs a map lookup and nothing more.
    const turn = takeTurn(key);
    if (turn === null) {
        return;
    }

    let account: IAuthAccount;
    let hostType: string;
    let path: string = filePath;
    try {
        if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined") {
            switch (Office.context?.diagnostics?.host) {
                case Office.HostType.Word:
                    hostType = "Word";
                    break;
                case Office.HostType.Excel:
                    hostType = "Excel";
                    break;
                case Office.HostType.PowerPoint:
                    hostType = "PowerPoint";
                    break;
                case Office.HostType.Outlook:
                    hostType = "Outlook";
                    break;
                default:
                    break;
            }
        }

        // The stored account, refreshing nothing: logging a failed refresh started another one, and
        // every log waited for whatever refresh was running (follow-up 2026-10-20).
        account = await AuthModule.getAccount(false);
    } catch (err) {
        // Ignore
    }

    const clientError = {
        error: error + turn.note,
        filePath: path,
        accountId: account?.username ?? account?.accountId,
        browser: navigator?.userAgent,
        hostType: hostType ?? "Other",
    }

    try {
        if (await apiClientError(clientError) === false) {
            turn.giveBack();
        }
    } catch (error) {
        turn.giveBack();
        console.error("Error on log save: ", error);
    }
}

export const logMessage = async (message: string, type?: string) => {
    try {
        const account: IAuthAccount = await AuthModule.getAccount(false);
        await apiLog(message, account, type);
    } catch (error) {
        console.error("Error on log message: ", error);
    }
}